import { useId, useMemo, useState, type ReactNode } from 'react';

import { AGE_MAX, AGE_MIN, ageBounds } from '@chat-emp/shared';
import type {
  Availability,
  EducationLevel,
  Gender,
  InboxFacet,
  Nationality,
} from '@chat-emp/shared';
import type { ApplicationStatus } from '../fixtures/domain.js';
import { Button } from '../components/Button.js';
import { Checkbox } from '../components/Checkbox.js';
import { Icon, type IconName } from '../components/Icon.js';
import { formatRinggit, plural } from '../util/format.js';
import { includesFold } from '../util/text.js';
import { useIsMobile } from '../util/useIsMobile.js';
import { JobTreeList } from './JobTreeList.js';
import type { JobTreeNode } from './job-tree.js';
import styles from './FilterDialog.module.css';

/** One row in the rail, and the page it opens. */
export type Facet =
  | 'job'
  | 'stage'
  | 'availability'
  | 'preferredLocation'
  | 'language'
  | 'skills'
  | 'salary'
  | 'education'
  | 'nationality'
  | 'gender'
  | 'age';

interface FacetDef {
  id: Facet;
  icon: IconName;
  label: string;
  /** The one line under the title. It names the fact the row reads, and stops. */
  blurb: string;
  /**
   * How the row reads inside the summary sentence, with its values to follow.
   * Plural and tenseless, because the subject is a set of people and a clause
   * that agreed with a singular would disagree the moment it was joined to the
   * next one.
   */
  verb: string;
  /** How one value reads after the verb, when its label alone would not. */
  say?: (label: string) => string;
}

/**
 * The rows, in the order an employer narrows: the posting first, then the
 * person who applied to it.
 *
 * Six sections in a 320px popover hanging off a button is the room problem that
 * made this a dialog in the first place.
 */
const FACETS: FacetDef[] = [
  {
    id: 'job',
    icon: 'briefcase',
    label: 'Job',
    blurb: 'Which posting they applied to.',
    verb: 'applied to',
  },
  {
    id: 'stage',
    icon: 'listChecks',
    label: 'Application status',
    blurb: 'Where they are in your pipeline.',
    verb: 'are',
  },
  {
    id: 'availability',
    icon: 'clockFading',
    label: 'Availability',
    blurb: 'How soon they could start.',
    verb: 'can start',
    say: (label) => (label === 'Flexible' ? 'whenever suits them' : label.toLowerCase()),
  },
  {
    id: 'preferredLocation',
    icon: 'mapPin',
    label: 'Location',
    // The two words this line exists for are "they" and "want". Without the
    // first it reads as the posting's cities, which is what this row used to
    // mean and what the job title on every chat still shows; without the second
    // it reads as where they live, the wrong fact about the right person.
    blurb: 'Where they want to work.',
    verb: 'want to work in',
  },
  {
    id: 'language',
    icon: 'languages',
    label: 'Speaking language',
    blurb: 'Languages they speak.',
    verb: 'speak',
  },
  {
    id: 'skills',
    icon: 'sparkles',
    label: 'Skills',
    blurb: 'Skills on their profile.',
    verb: 'are skilled in',
  },
  {
    id: 'salary',
    icon: 'wallet',
    label: 'Expected salary',
    blurb: 'The salary they are asking for.',
    verb: 'expect',
  },
  {
    id: 'education',
    icon: 'graduationCap',
    label: 'Highest education',
    blurb: 'How far they got in school.',
    verb: 'finished',
  },
  {
    id: 'nationality',
    icon: 'globe',
    label: 'Nationality',
    blurb: 'Whether they are Malaysian.',
    verb: 'are',
  },
  {
    id: 'gender',
    icon: 'users',
    label: 'Gender',
    blurb: 'As stated on their profile.',
    verb: 'are',
  },
  {
    id: 'age',
    icon: 'calendar',
    label: 'Age',
    blurb: 'As stated on their profile.',
    verb: 'are aged',
  },
];

/** The rows only a posting can answer. See `FilterDialogProps.jobRows`. */
const JOB_ROWS = new Set<Facet>(['job', 'stage']);

/**
 * The page's search box appears on rows whose list is open-ended. The two fixed
 * ladders never need one. Job's reads both lines of a row, so "Ipoh" and "Sales
 * Executive" are the same question asked two ways.
 */
const SEARCH_LABELS: Partial<Record<Facet, string>> = {
  job: 'Search job title or location',
  preferredLocation: 'Search places',
  skills: 'Search skills',
};

/**
 * A page that is a list of ticks with counts — every facet but Job and Salary.
 *
 * Four rows in one shape rather than twelve props, and the reason is that they
 * really are one thing: an option carries a key, a label and the number of rows
 * it would keep, whether it is a place, a language, a skill or a rung.
 *
 * Job stays out because a two-level tree is not a list, and Salary because a
 * range has no options to count.
 */
export interface TickFacet<T extends string = string> {
  options: InboxFacet[];
  selected: readonly T[];
  onToggle: (key: T) => void;
  /**
   * Conversations whose candidate says nothing on this dimension, when the
   * dimension is one that can be silent. See `UnknownNote`.
   */
  unknownCount?: number;
}

/** Both ends of a range, either of which may be open. */
export interface NumberRange {
  min?: number;
  max?: number;
}

interface FilterDialogProps {
  /** False in Direct Messages, where no posting exists to answer Job or
   *  Application status. Invalid rows, not merely empty ones. */
  jobRows: boolean;
  jobTree: JobTreeNode[];
  selectedJobIds: string[];
  onToggleJob: (id: string) => void;
  /** Take or put down several jobs at once — whatever the page is showing. */
  onSelectManyJobs: (ids: string[], on: boolean) => void;
  stage: TickFacet<ApplicationStatus>;
  availability: TickFacet<Availability>;
  preferredLocation: TickFacet;
  language: TickFacet;
  skills: TickFacet;
  education: TickFacet<EducationLevel>;
  nationality: TickFacet<Nationality>;
  gender: TickFacet<Gender>;
  salary: { range: NumberRange; onChange: (range: NumberRange) => void };
  age: { range: NumberRange; onChange: (range: NumberRange) => void };
  /** Put down everything this dialog sets. */
  onClearAll: () => void;
  /**
   * What the list holds right now. The dialog covers the column it filters, so
   * this is the only thing on screen reporting the effect of a pick. Null until
   * the first count lands — the card and the button then drop the number rather
   * than claim one they do not have.
   */
  results: { shown: number | null };
  onClose: () => void;
}

/**
 * What a row has in force, said in one line: the first value, and how many more.
 *
 * A value is not a control; it is the answer to what the row is set to, and a
 * row states that on its right the way every settings list does. Dropping one
 * happens where it was picked, one press further in; dropping all of them is
 * what "Clear all" is for.
 */
function summarise(values: string[]): string | null {
  const [first, ...rest] = values;
  if (first === undefined) return null;
  return rest.length > 0 ? `${first} +${rest.length}` : first;
}

/**
 * One row's values as the summary sentence says them: "English, Bahasa Malaysia
 * or Mandarin".
 *
 * "Or" is the whole point. A column of ticks looks identical whether it wants
 * all three or any of them, and the rule was never written anywhere. Rather
 * than state it, the sentence uses the word.
 *
 * No comma before the "or" — these are short values in a sentence that already
 * carries commas between its clauses. The clause joiner takes the opposite
 * view, and for the same reason.
 */
function orList(values: string[]): string {
  if (values.length <= 1) return values[0] ?? '';
  return `${values.slice(0, -1).join(', ')} or ${values[values.length - 1]}`;
}

/**
 * The funnel, as a screen of its own.
 *
 * It was a 320px popover hanging off its button, and the room was the problem:
 * a job tree that opens families, a place chip per branch, and four more rows
 * to come, all inside a box the width of a phone.
 *
 * A rail of what there is to set, and a page for whichever one is being set.
 * The rail is the part that earns the dialog: every row carries what it has in
 * force on its right, so the answer to "what is filtering this list" is the
 * first thing on screen rather than sections to scroll through and compare.
 *
 * A phone gets the same two halves one at a time: the rail is the screen, and a
 * row opens into itself. Nothing is stacked into one long scroll, so the
 * summary stays a summary.
 *
 * Picks land on the list as they are made, which the footer's count shows.
 * There is no Apply, because there is nothing to apply.
 */
export function FilterDialog({
  jobRows,
  jobTree,
  selectedJobIds,
  onToggleJob,
  onSelectManyJobs,
  stage,
  availability,
  preferredLocation,
  language,
  skills,
  education,
  nationality,
  gender,
  salary,
  age,
  onClearAll,
  results,
  onClose,
}: FilterDialogProps) {
  const titleId = useId();
  const isMobile = useIsMobile();

  /**
   * Which row is open, and one at a time either way — the same state serving
   * two shapes of one idea. Beside the rail on a desktop it picks which page
   * shows; on a phone there is nowhere to put a page, so the row opens into
   * itself and the one before it closes, because that is what holding a single
   * id does.
   *
   * Null means nothing is open, which only a phone can show: a desktop always
   * has a page up and falls back to the first row.
   */
  const [open, setOpen] = useState<Facet | null>(null);
  const rows = jobRows ? FACETS : FACETS.filter((facet) => !JOB_ROWS.has(facet.id));
  const active = open ?? rows[0]!.id;

  /**
   * What the page's search box holds. One box is ever on screen, so one piece
   * of state — and it is put down whenever the open row changes, because a word
   * typed to find a place is not a word anybody meant to carry anywhere else.
   */
  const [query, setQuery] = useState('');
  const openFacet = (id: Facet) => {
    setQuery('');
    setOpen((current) => (isMobile && current === id ? null : id));
  };

  // Every job on offer by id, so a picked one can be named in the rail without
  // walking the tree per row.
  const jobTitles = useMemo(() => {
    const titles = new Map<string, string>();
    for (const node of jobTree) {
      titles.set(node.parent.id, node.parent.title);
      for (const child of node.children) titles.set(child.id, child.title);
    }
    return titles;
  }, [jobTree]);

  // Generic in the key, not `TickFacet<string>`: a facet whose keys are a union
  // is not a facet whose keys are strings, because `onToggle` runs the wrong way
  // round for that to be safe.
  const labelsOf = <T extends string>(facet: TickFacet<T>): string[] =>
    facet.selected.map((key) => facet.options.find((option) => option.key === key)?.label ?? key);

  /** What each row has in force. The rail prints it; nothing else derives it. */
  const range = salaryLabel(salary.range);
  const ages = ageLabel(ageBounds(age.range));
  const applied: Record<Facet, string[]> = {
    job: selectedJobIds.map((id) => jobTitles.get(id) ?? 'This job'),
    stage: labelsOf(stage),
    availability: labelsOf(availability),
    preferredLocation: labelsOf(preferredLocation),
    language: labelsOf(language),
    skills: labelsOf(skills),
    // One value or none — a range is a single answer, not a set of picks.
    salary: range ? [range] : [],
    education: labelsOf(education),
    nationality: labelsOf(nationality),
    gender: labelsOf(gender),
    age: ages ? [ages] : [],
  };

  // Read off what is in force rather than tracked, so it cannot drift from it.
  const anyActive = rows.some((facet) => applied[facet.id].length > 0);
  // In rail order, so a clause sits in the sentence where its row sits above
  // it and neither has to be hunted for against the other.
  const activeFacets = rows.filter((facet) => applied[facet.id].length > 0);

  const searchLabel = (id: Facet): string | undefined => SEARCH_LABELS[id];
  // Only the open page is filtered by its box — see `openFacet`, which empties
  // the box on the way in, so this can never be another row's word.
  const needleFor = (id: Facet) =>
    id === active && searchLabel(id) ? query.trim().toLowerCase() : '';

  const activeSearch = searchLabel(active);

  const pages: Record<Facet, ReactNode> = {
    job: (
      <JobTreeList
        tree={jobTree}
        needle={needleFor('job')}
        selected={selectedJobIds}
        onToggle={onToggleJob}
        onSelectMany={onSelectManyJobs}
        emptyNote="Nothing here is bound to a posting."
      />
    ),
    stage: (
      <TickPage
        {...stage}
        empty="Nothing here has an application."
        noun="stage"
        needle={needleFor('stage')}
      />
    ),
    availability: (
      <TickPage
        {...availability}
        empty="Nobody here says when they could start."
        noun="availability"
        needle={needleFor('availability')}
      />
    ),
    /*
     * The same tick rows as the jobs, and for the same reason: an employer
     * hiring nationwide lists a place per branch, and a long set of values is
     * read down a column, not along a wrapping run of pills.
     *
     * This is where the run of chips went, along with the two-line clamp and
     * the "show n more" behind it. All of that was the 320px popover's problem —
     * there is a page here now, and it scrolls.
     */
    preferredLocation: (
      <TickPage
        {...preferredLocation}
        empty="Nobody here says where they want to work."
        noun="place"
        needle={needleFor('preferredLocation')}
      />
    ),
    language: (
      <TickPage
        {...language}
        empty="Nobody here lists a language."
        noun="language"
        needle={needleFor('language')}
      />
    ),
    skills: (
      <TickPage
        {...skills}
        empty="Nobody here lists a skill."
        noun="skill"
        needle={needleFor('skills')}
      />
    ),
    salary: (
      <RangePage
        range={salary.range}
        onChange={salary.onChange}
        prefix="RM"
        format={(value) => value.toLocaleString('en-MY')}
        placeholders={['Insert min. salary', 'Insert max. salary']}
        crossedNote="The minimum is above the maximum, so nothing can match."
        hint={salarySummary(salary.range)}
      />
    ),
    education: (
      <TickPage
        {...education}
        empty="Nobody here says how far they got in school."
        noun="level"
        needle={needleFor('education')}
      />
    ),
    nationality: (
      <TickPage
        {...nationality}
        empty="Nobody here says their nationality."
        noun="nationality"
        needle={needleFor('nationality')}
      />
    ),
    age: (
      <RangePage
        range={age.range}
        onChange={age.onChange}
        placeholders={['Min age', 'Max age']}
        maxDigits={3}
        crossedNote="Max age cannot be lower than min age."
        crossed={ageCrossed(age.range)}
        warn={
          !ageCrossed(age.range) && [age.range.min, age.range.max].some(ageDead)
            ? `Ages run from ${AGE_MIN} to ${AGE_MAX}.`
            : null
        }
      />
    ),
    gender: (
      <TickPage
        {...gender}
        empty="Nobody here states their gender."
        noun="gender"
        needle={needleFor('gender')}
      />
    ),
  };

  const renderRow = (facet: FacetDef) => {
    const value = summarise(applied[facet.id]);
    // On a phone the row holds its own controls, so it is open rather than
    // current, and pressing it again shuts it. A desktop always has one page up:
    // pressing the row it is already showing must not empty the pane.
    const expandedRow = isMobile && open === facet.id;
    const rowSearch = searchLabel(facet.id);
    const on = isMobile ? expandedRow : active === facet.id;
    return (
      <li key={facet.id} className={on ? styles.rowOn : styles.row}>
        <button
          type="button"
          className={styles.rowTrigger}
          aria-expanded={isMobile ? expandedRow : undefined}
          onClick={() => openFacet(facet.id)}
        >
          <Icon name={facet.icon} size={16} />
          <span className={styles.rowLabel}>{facet.label}</span>
          {value && <span className={styles.rowValue}>{value}</span>}
          {/* Right on a desktop, where it opens a page over there. On a phone
              the row grows in place, so it wears the fold's own pair: apart to
              open, together to close. */}
          <Icon
            name={isMobile ? (expandedRow ? 'chevronsDownUp' : 'chevronsUpDown') : 'chevronRight'}
            size={15}
          />
        </button>
        {isMobile && expandedRow && (
          <div className={styles.fold}>
            <p className={styles.inlineBlurb}>{facet.blurb}</p>
            {rowSearch && (
              <SearchBox
                className={styles.search}
                placeholder={rowSearch}
                value={query}
                onChange={setQuery}
              />
            )}
            {pages[facet.id]}
          </div>
        )}
      </li>
    );
  };

  const current = rows.find((facet) => facet.id === active) ?? rows[0]!;

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className={styles.header}>
          {/* The glyph off the button that opened this, so the dialog says where
              it came from. */}
          <span className={styles.iconBadge}>
            <Icon name="filter" size={18} />
          </span>
          <h2 className={styles.title} id={titleId}>
            Filter
          </h2>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
            <Icon name="close" size={20} />
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.rail}>
            <div className={styles.railHead}>
              <p className={styles.railTitle}>Filters</p>
            </div>
            <ul className={styles.rowList}>{rows.map(renderRow)}</ul>

            {/* The answer to "is it or, or and": both words are in the sentence,
                so the rule is shown rather than taught, next to the rows that
                produced it. Only while something is set — with nothing on it
                would read "Jobseekers who", and the empty rows already say
                nothing is on. */}
            {activeFacets.length > 0 && (
              <div className={styles.summaryBox}>
                <p className={styles.summaryTitle}>You are looking for:</p>
                <p className={styles.summary}>
                  Jobseekers who{' '}
                  {activeFacets.map((facet, index) => (
                    <span key={facet.id}>
                      {/* Two clauses join bare; three or more take commas and a
                          serial comma before the "and", which is what keeps the
                          sentence readable when the clauses have "or" inside
                          them — "speak English or Mandarin and are skilled in
                          Figma" otherwise hangs the Figma off the Mandarin. */}
                      {index > 0 &&
                        (index === activeFacets.length - 1
                          ? activeFacets.length > 2
                            ? ', and '
                            : ' and '
                          : ', ')}
                      {facet.verb}{' '}
                      <strong>
                        {orList(applied[facet.id].map(facet.say ?? ((label) => label)))}
                      </strong>
                    </span>
                  ))}
                  .
                </p>
                <MatchCount shown={results.shown} />
              </div>
            )}
          </div>

          {/* The other half, which only a desktop has room for. A phone opens
              each row into itself instead — see `renderRow`. */}
          {!isMobile && (
            <div className={styles.page}>
              <div className={styles.pageHead}>
                <p className={styles.pageTitle}>{current.label}</p>
                <p className={styles.pageBlurb}>{current.blurb}</p>
              </div>
              {/* Above the scroller, not inside it: a control that shortens a
                  list is not an item of it, and sticky-inside leaves half a row
                  showing through the gap above it. */}
              {activeSearch && (
                <SearchBox
                  className={styles.pageSearch}
                  placeholder={activeSearch}
                  value={query}
                  onChange={setQuery}
                />
              )}
              {/* Keyed by the row it belongs to, so switching rows mounts a
                  fresh page rather than handing the next one the last one's
                  state. */}
              <div className={styles.pageBody} key={active}>
                {pages[active]}
              </div>
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <div className={styles.actions}>
            {/* The quietest control here: nothing is made by pressing it, and an
                accent fill would read as the thing to do on a dialog whose whole
                job is above it. */}
            <Button variant="ghost" disabled={!anyActive} onClick={onClearAll}>
              Clear all
            </Button>
            {/* "Show", not "Apply": picks land on the list as they are made, so
                there is nothing to commit and what pressing this does is uncover
                the column. Live at zero, deliberately — no-results is when an
                employer most wants back to that column, and disabling the way
                out leaves them an X and a Clear all that throws the work away.

                Without a count yet it drops the number rather than saying nought
                over a column about to show twelve. */}
            <Button onClick={onClose}>
              {results.shown === null
                ? 'Show jobseekers'
                : `Show ${plural(results.shown, 'jobseeker')}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * What the criteria above come to, closing the card they open.
 *
 * Verb first, so one match and many take the same verb — "Matches 1 jobseeker"
 * and "Matches 24 jobseekers" rather than a line that has to conjugate.
 *
 * Neutral until it is zero. Red on a filter doing exactly what it was told
 * reports a fault that isn't there, and this palette spends red on one thing:
 * nothing left is the single reading where the employer has to act.
 *
 * Nothing at all while the count is in flight. Before the first answer lands
 * there is no number, and "Matches 0 jobseekers" over a column about to show
 * twelve is the one wrong answer that looks right.
 */
function MatchCount({ shown }: { shown: number | null }) {
  if (shown === null) return null;
  if (shown === 0) {
    return (
      <p className={`${styles.summaryCount} ${styles.summaryCountNone}`}>No jobseekers match.</p>
    );
  }
  return <p className={styles.summaryCount}>Matches {plural(shown, 'jobseeker')}.</p>;
}

/**
 * A list of ticks with counts. One component for every row whose page is a
 * list, because they differ in nothing a reader would want them to differ in:
 * an option carries a key, a label and the number of rows it would keep.
 */
function TickPage<T extends string>({
  options,
  selected,
  onToggle,
  unknownCount,
  empty,
  noun,
  needle,
}: TickFacet<T> & {
  /** What to say when the set being filtered offers nothing here. */
  empty: string;
  /** What one row is, for the "no matches" line under a search. */
  noun: string;
  /** What the page's search box holds, lowercased. Empty when there is none. */
  needle: string;
}) {
  /*
   * Filtered by substring, and a ticked row survives the filter whatever is
   * typed. A search that hid a pick would leave the list narrowed with nothing
   * on the page to say why — the same reflex that auto-opens a job family
   * around a selected sub-role.
   */
  const shown =
    needle === ''
      ? options
      : options.filter(
          (option) =>
            includesFold(option.label || option.key, needle) || selected.includes(option.key as T),
        );

  if (options.length === 0) return <p className={styles.empty}>{empty}</p>;
  if (shown.length === 0) {
    return <p className={styles.empty}>{`No ${noun} matches “${needle}”.`}</p>;
  }

  return (
    <>
      <ul className={styles.tickList}>
        {shown.map((option) => {
          const key = option.key as T;
          return (
            <li key={option.key}>
              <label className={styles.tickRow}>
                <Checkbox checked={selected.includes(key)} onChange={() => onToggle(key)} />
                <span className={styles.tickText}>
                  <span className={styles.tickLabel}>{option.label || option.key}</span>
                  <span className={styles.count}>({option.count})</span>
                </span>
              </label>
            </li>
          );
        })}
      </ul>
      <UnknownNote count={unknownCount} noun={noun} />
    </>
  );
}

/**
 * How many conversations this row cannot speak for.
 *
 * Coverage in the candidate index is thin — skills sit near a quarter — and a
 * candidate with no value drops out the moment a pick is in force. Without this
 * line the counts read as the whole truth: an employer ticking one skill sees a
 * dozen rows and concludes a dozen people have it, when most of the inbox was
 * never asked. The count says what a pick keeps; this says what it could never
 * have kept either way.
 *
 * Absent and zero are different answers — the column-backed rows send nothing
 * and must render nothing, rather than claiming everybody is accounted for.
 */
function UnknownNote({ count, noun }: { count?: number; noun: string }) {
  if (count === undefined || count === 0) return null;
  const subject = count === 1 ? 'has' : 'have';
  return (
    <p className={styles.unknownNote}>
      {`${plural(count, 'conversation')} ${subject} no ${noun} on file, so a pick here sets ${
        count === 1 ? 'it' : 'them'
      } aside.`}
    </p>
  );
}

/** The range as the rail prints it. Either end can be open, and usually is. */
function salaryLabel({ min, max }: NumberRange): string | null {
  if (min !== undefined && max !== undefined)
    return `${formatRinggit(min)} – ${formatRinggit(max)}`;
  if (min !== undefined) return `From ${formatRinggit(min)}`;
  if (max !== undefined) return `Up to ${formatRinggit(max)}`;
  return null;
}

/**
 * The range read back in words under the boxes. Null while both are empty —
 * there is no range to state, and a line under an untouched control is a line
 * nobody asked for.
 *
 * Three sentences rather than one with holes in it: "between x and y", "at
 * least x" and "up to x" are how a range is spoken, and a single template that
 * grew clauses would be the one place this is allowed to read like a form.
 */
function salarySummary({ min, max }: NumberRange): string | null {
  if (min !== undefined && max !== undefined) {
    return `Showing salary between ${formatRinggit(min)} and ${formatRinggit(max)}`;
  }
  if (min !== undefined) return `Showing salary at least ${formatRinggit(min)}`;
  if (max !== undefined) return `Showing salary up to ${formatRinggit(max)}`;
  return null;
}

/**
 * The pages that are not a list: two boxes, because a range is one answer
 * rather than a set of picks.
 *
 * A slider would want the pool's own low and high ends to put its handles
 * between, and upstream does not send them — real expectations bunch under
 * RM5,000 while the raw field carries sentinels in the millions, so a slider
 * scaled to the data would be unusable and one scaled to the sentinel would be
 * a control whose whole range is one pixel wide. Two boxes need neither.
 */
function RangePage({
  range,
  onChange,
  prefix,
  format = String,
  placeholders,
  maxDigits,
  crossedNote,
  crossed = range.min !== undefined && range.max !== undefined && range.min > range.max,
  warn,
  hint,
}: {
  range: NumberRange;
  onChange: (range: NumberRange) => void;
  prefix?: string;
  format?: (value: number) => string;
  placeholders: [string, string];
  maxDigits?: number;
  crossedNote: string;
  /** Defaults to the raw boxes; a row that drops some values decides for itself. */
  crossed?: boolean;
  warn?: string | null;
  hint?: string | null;
}) {
  /*
   * A number, or absent for an empty box — never NaN and never 0. An empty box
   * means "no bound", and a `Number('')` of 0 would silently become a floor
   * that keeps everybody or a ceiling that keeps nobody.
   */
  const set = (end: 'min' | 'max') => (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, maxDigits);
    const next = { ...range };
    if (digits === '') delete next[end];
    else next[end] = Number(digits);
    onChange(next);
  };

  // Both ends the wrong way round keeps nobody, and the footer's "0 of 231" is
  // technically the answer. Saying so here is cheaper than making the employer
  // work out which of the two boxes to fix.
  const anySet = range.min !== undefined || range.max !== undefined;
  const warning = crossed ? crossedNote : warn;

  const box = (end: 'min' | 'max', label: string, placeholder: string) => (
    <label className={styles.salaryField}>
      <span className={styles.salaryFieldLabel}>{label}</span>
      <span className={styles.salaryInput}>
        {prefix && <span className={styles.salaryPrefix}>{prefix}</span>}
        {/* An instruction, not an example: a number in a grey box beside a
            "RM" reads as one already typed, and the first move is to clear a
            filter that was never set. */}
        <input
          type="text"
          inputMode="numeric"
          value={range[end] === undefined ? '' : format(range[end])}
          placeholder={placeholder}
          onChange={(event) => set(end)(event.target.value)}
        />
      </span>
    </label>
  );

  return (
    <div className={styles.salary}>
      <div className={styles.salaryFields}>
        {box('min', 'Minimum', placeholders[0])}
        <span className={styles.salaryDash} aria-hidden="true">
          –
        </span>
        {box('max', 'Maximum', placeholders[1])}
      </div>

      {warning ? (
        <p className={styles.salaryWarn}>{warning}</p>
      ) : (
        hint && <p className={styles.salaryHint}>{hint}</p>
      )}

      {anySet && (
        <button type="button" className={styles.salaryReset} onClick={() => onChange({})}>
          Clear range
        </button>
      )}
    </div>
  );
}

const inAgeRange = (v?: number): v is number => v !== undefined && v >= AGE_MIN && v <= AGE_MAX;

/** Crossed only when both boxes hold real ages; otherwise `ageBounds` drops one and applies the other. */
function ageCrossed({ min, max }: NumberRange): boolean {
  return inAgeRange(min) && inAgeRange(max) && min > max;
}

/** A bound no further typing can rescue: "2" may still become "25", "13" and "150" cannot. */
function ageDead(v?: number): boolean {
  return v !== undefined && (v > AGE_MAX || (v < AGE_MIN && v * 10 > AGE_MAX));
}

function ageLabel({ min, max }: NumberRange): string | null {
  if (min !== undefined && max !== undefined) return `${min} – ${max}`;
  if (min !== undefined) return `${min} or older`;
  if (max !== undefined) return `${max} or younger`;
  return null;
}

function SearchBox({
  placeholder,
  value,
  onChange,
  className,
}: {
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  /** Undefined-able because a CSS module lookup is, under strict indexing. */
  className: string | undefined;
}) {
  return (
    <div className={className}>
      <Icon name="search" size={15} />
      <input
        className={styles.searchInput}
        type="search"
        value={value}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}
